import { objectWithoutProperties as _objectWithoutProperties, objectSpread2 as _objectSpread2 } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import get from 'lodash/get.js';
import DOMPurify from 'dompurify';
import PendoButton from '../../../components/button/pendo-button.vue.js';
import PendoCard from '../../../components/card/pendo-card.vue.js';
import PendoChooserGrid from '../../../components/chooser-grid/pendo-chooser-grid.vue.js';
import PendoIcon from '../../../components/icon/pendo-icon.vue.js';
import PendoInput from '../../../components/input/pendo-input.vue.js';
import PendoModal from '../../../components/modal/pendo-modal.vue.js';
import PendoTooltip from '../../../directives/tooltip/pendo-tooltip.js';
import PendoMultiselect from '../../../components/multiselect/pendo-multiselect.vue.js';
import PendoIconOption from '../../../components/multiselect/option-types/pendo-icon-option.vue.js';
import PendoThemeSwatch from '../theme-swatch/pendo-theme-swatch.vue.js';

const _excluded = ["allowActions", "tags", "buildingBlocks", "appId"];
var script = {
  name: 'PendoThemeGrid',
  components: {
    PendoCard,
    PendoChooserGrid,
    PendoButton,
    PendoIcon,
    PendoIconOption,
    PendoInput,
    PendoModal,
    PendoMultiselect,
    PendoThemeSwatch
  },
  directives: {
    PendoTooltip
  },
  inheritAttrs: false,
  props: {
    title: {
      type: String,
      default: null
    },
    themes: {
      type: Array,
      required: true
    },
    apps: {
      type: Array,
      default: () => []
    },
    disabled: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    allowActions: {
      type: Object,
      default: () => ({})
    },
    filters: {
      type: [Object, Array],
      default: () => [{
        prop: ['title', 'subtitle'],
        value: ''
      }]
    }
  },
  data() {
    return {
      showModal: false,
      pendingTheme: null,
      pendingAction: null
    };
  },
  computed: {
    modalConfig() {
      if (!this.pendingAction) {
        return {};
      }
      return {
        delete: {
          title: 'Delete Theme',
          confirmButtonConfig: {
            type: 'danger',
            label: 'Delete Theme'
          }
        },
        duplicate: {
          title: 'Duplicate Theme',
          confirmButtonConfig: {
            label: 'Duplicate Theme'
          }
        }
      }[this.pendingAction];
    },
    formattedApps() {
      const {
        apps,
        pendingTheme
      } = this;
      if (!apps) {
        return [];
      }
      return apps.filter(app => app.platform === pendingTheme.app.platform);
    },
    formattedThemes() {
      const {
        loading,
        themes,
        disabled,
        apps
      } = this;
      if (loading && !themes.length) {
        return Array(6).fill({
          disabled: true,
          loading: true
        });
      }
      return themes.map(theme => {
        let app = {};
        const {
            allowActions = {},
            tags,
            buildingBlocks,
            appId
          } = theme,
          rest = _objectWithoutProperties(theme, _excluded);
        if (apps && apps.length) {
          app = apps.find(_ref => {
            let {
              id
            } = _ref;
            return id === appId;
          }) || {};
        }
        const swatch = this.buildSwatch(buildingBlocks);
        return _objectSpread2(_objectSpread2({}, rest), {}, {
          app: {
            id: appId,
            displayName: app.displayName || app.name,
            platform: app && app.platform || 'web',
            iconType: this.getIconType(app)
          },
          allowActions: {
            duplicate: this.allowActions.duplicate || allowActions.duplicate,
            delete: (this.allowActions.delete || allowActions.delete) && !tags.toLowerCase().includes('default')
          },
          buildingBlocks,
          title: DOMPurify.sanitize(theme.name),
          subtitle: `Created By: ${theme.createdByUser && theme.createdByUser.username}`,
          disabled: loading || theme.loading || disabled,
          loading: loading || theme.loading,
          swatch
        });
      });
    }
  },
  methods: {
    getIconType(app) {
      if (app) {
        return app.platform === 'extension' ? 'puzzle-piece' : app.platform;
      }
      return '';
    },
    buildSwatch(theme) {
      const container = get(theme, 'container.background', '#FFFFFF');
      const backdrop = get(theme, 'backdrop.background', '#FFFFFF');
      const primaryButton = get(theme, 'primaryButton.background', '#FFFFFF');
      const secondaryButton = get(theme, 'secondaryButton.background', '#FFFFFF');
      const title = get(theme, 'title.fontColor', '#FFFFFF');
      return [container, backdrop, primaryButton, secondaryButton, title];
    },
    resetPendingActionState() {
      this.pendingAction = null;
      this.pendingTheme = null;
      this.showModal = false;
    },
    promptConfirmDelete(theme) {
      this.pendingAction = 'delete';
      this.pendingTheme = theme;
      this.showModal = true;
    },
    promptConfirmDuplicate(theme) {
      const {
        app,
        archive,
        attributes,
        buildingBlocks,
        name
      } = theme;
      this.pendingAction = 'duplicate';
      this.pendingTheme = {
        archive,
        attributes,
        buildingBlocks,
        tags: 'custom',
        name: `Copy of ${name}`,
        originalName: `Copy of ${name}`,
        app
      };
      this.showModal = true;
    },
    confirmPendingAction() {
      return {
        delete: this.deleteTheme,
        duplicate: this.duplicateTheme
      }[this.pendingAction]();
    },
    selectTheme(selected) {
      const themeToEmit = this.themes.find(theme => theme.id === selected.id);
      this.$emit('select-theme', themeToEmit);
    },
    deleteTheme() {
      this.$emit('delete-theme', this.pendingTheme);
      this.resetPendingActionState();
    },
    duplicateTheme() {
      const {
        archive,
        attributes,
        buildingBlocks,
        tags = 'custom',
        name,
        originalName,
        app
      } = this.pendingTheme;
      const newTheme = {
        archive,
        attributes,
        buildingBlocks,
        tags,
        name: name || originalName,
        appId: app.id
      };
      this.$emit('duplicate-theme', newTheme);
      this.resetPendingActionState();
    },
    onVisibleItemsChange(visibleItemsChange) {
      this.$emit('visible-items-change', visibleItemsChange);
    }
  }
};

export { script as default };
//# sourceMappingURL=pendo-theme-grid.vue2.js.map
